import { CommonModule } from '@angular/common';
import { Component, input, output, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { EmissionSourceHierarchyResponseModel, EmissionTypeModel, FormulaSearchModel } from '@features/forms/models/emission-source.model';
import { GlobalWarmingPotentialModel } from '@features/forms/models/global-warming-potential.model';
import { Tooltip } from "primeng/tooltip";
import { MessageService } from 'primeng/api';
import { Toast } from "primeng/toast";
import { Message } from "primeng/message";
import { UnsavedChangesService } from '@features/forms/services/unsaved-changes.service';

interface SelectOption<T = any> {
  label: string;
  value: T;
  items?: any[];
  ids?: string[];
  originalValue?: string;
  emissionTypeSourceMap?: Map<string, string>;
}

interface GroupedSource {
  name: string;
  ids: string[];
  emissionTypes: EmissionTypeModel[];
  scopeCategory: string;
  emissionTypeSourceMap: Map<string, string>;
}

interface SourcesMap {
  sources: Record<string, string>;
  variations: Record<string, string>;
  references: Record<string, string>;
}

@Component({
  selector: 'app-form-filter',
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    ButtonModule,
    ReactiveFormsModule,
    Tooltip,
    Toast,
    Message
  ],
  templateUrl: './form-filter.html',
  styleUrl: './form-filter.scss',
  providers: [MessageService]
})
export class FormFilter implements OnInit, OnChanges {
  private readonly messageService = inject(MessageService);
  private readonly http = inject(HttpClient);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  loading = input<boolean>(false);
  disableDownload = input<boolean>(true);
  isDownloading = input<boolean>(false);
  emissionSourceHierarchy = input<EmissionSourceHierarchyResponseModel | null>(null);
  globalWarmingPotential = input<GlobalWarmingPotentialModel[]>([]);
  mode = input<'none' | 'manual' | 'excel'>('none');

  search = output<FormulaSearchModel>();
  addNewActivity = output<void>();
  gwpChange = output<string>();
  download = output<void>();
  downloadTemplate = output<void>();
  uploadExcel = output<File>();

  emissionSourceOptions: SelectOption[] = [];
  emissionTypeOptions: SelectOption[] = [];
  referenceOptions: { label: string; value: string }[] = [];
  yearOptions: SelectOption<number>[] = [];

  selectedEmissionSource: SelectOption | null = null;
  selectedEmissionType: SelectOption | null = null;
  selectedReference: string = '';
  selectedGlobalWarmingPotential: string = '';
  selectedYear: number | null = null;

  isSubmitted = false;
  sourcesMap: SourcesMap | null = null;

  private globalEmissionTypeSourceMap = new Map<string, string>();

  ngOnInit(): void {
    this.initializeYearOptions();
    this.setDefaultSelectedGwp();
    this.loadSourcesMap();
  }

  /**
   * Carga el diccionario sources.json
   */
  private loadSourcesMap(): void {
    this.http.get<SourcesMap>('assets/data/sources.json').subscribe({
      next: (map) => {
        this.sourcesMap = map;
        // Si ya hay jerarquía cargada, reinicializar las opciones con el diccionario
        if (this.emissionSourceHierarchy()) {
          this.initializeEmissionSourceOptions();
        }
      },
      error: () => {
        // Fallback: continuar sin diccionario
        this.sourcesMap = {
          sources: {},
          variations: {},
          references: {}
        };
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['emissionSourceHierarchy'] && this.emissionSourceHierarchy()) {
      this.initializeEmissionSourceOptions();
    }
  }

  /**
   * Inicializa las opciones del select de años
   */
  private initializeYearOptions(): void {
    const currentYear = new Date().getFullYear();
    const startYear = 2021;

    this.yearOptions = [];

    // Generar array de años desde el año actual hasta 2021
    for (let year = currentYear; year >= startYear; year--) {
      this.yearOptions.push({
        label: year.toString(),
        value: year
      });
    }

    // Si no hay año seleccionado, establecer el año actual como seleccionado por defecto
    if (!this.selectedYear) {
      this.selectedYear = currentYear;
    }
  }

  setDefaultSelectedGwp(): void {
    if (this.globalWarmingPotential().length > 0) {
      const ar6 = this.globalWarmingPotential().find(gwp => gwp.Reference.toLowerCase() === 'ar6');
      this.selectedGlobalWarmingPotential = ar6?.Id || '';
    }
  }

  /**
   * Obtiene el valor traducido del diccionario o retorna el valor original
   */
  private getTranslatedValue(key: string, dictionary: Record<string, string> | undefined): string {
    if (!dictionary || !key) return key;
    return dictionary[key] || key;
  }

  /**
   * Inicializa las opciones del select Fuentes de emisión
   */
  private initializeEmissionSourceOptions(): void {
    const hierarchy = this.emissionSourceHierarchy();
    if (!hierarchy?.Scopes) return;

    const sourcesByName = new Map<string, GroupedSource>();

    for (const scope of hierarchy.Scopes) {
      for (const category of scope.Categories) {
        const scopeCategory = `${scope.Name} - ${category.Name}`;

        for (const source of category.Sources) {
          const key = `${scopeCategory}|${source.Name}`;

          if (!sourcesByName.has(key)) {
            const emissionTypeSourceMap = new Map<string, string>();
            source.EmissionTypes.forEach(et => {
              et.References.forEach(ref => {
                const mapKey = `${et.Type}|${et.Variation || ''}|${ref}`;
                emissionTypeSourceMap.set(mapKey, source.Id);
              });
            });

            sourcesByName.set(key, {
              name: source.Name,
              ids: [source.Id],
              emissionTypes: [...source.EmissionTypes],
              scopeCategory,
              emissionTypeSourceMap
            });
          } else {
            const existing = sourcesByName.get(key)!;
            existing.ids.push(source.Id);
            source.EmissionTypes.forEach(et => {
              et.References.forEach(ref => {
                const mapKey = `${et.Type}|${et.Variation || ''}|${ref}`;
                existing.emissionTypeSourceMap.set(mapKey, source.Id);
              });
            });
            existing.emissionTypes.push(...source.EmissionTypes);
          }
        }
      }
    }

    const groupedByCategory = new Map<string, SelectOption[]>();

    sourcesByName.forEach(source => {
      if (!groupedByCategory.has(source.scopeCategory)) {
        groupedByCategory.set(source.scopeCategory, []);
      }

      const translatedName = this.getTranslatedValue(
        source.name,
        this.sourcesMap?.sources
      );

      groupedByCategory.get(source.scopeCategory)!.push({
        label: translatedName,
        value: source.name,
        originalValue: source.name,
        ids: source.ids,
        items: source.emissionTypes,
        emissionTypeSourceMap: source.emissionTypeSourceMap
      });
    });

    this.emissionSourceOptions = Array.from(groupedByCategory.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([categoryLabel, items]) => ({
        label: categoryLabel,
        value: categoryLabel,
        items: items.sort((a, b) => a.label.localeCompare(b.label))
      }));

    this.globalEmissionTypeSourceMap = new Map<string, string>();
    sourcesByName.forEach(source => {
      source.emissionTypeSourceMap.forEach((sourceId, key) => {
        this.globalEmissionTypeSourceMap.set(key, sourceId);
      });
    });

    this.initializeAllEmissionTypeOptions(sourcesByName);
  }

  private initializeAllEmissionTypeOptions(sourcesByName: Map<string, GroupedSource>): void {
    const typesByScopeCategory = new Map<
      string,
      Map<string, { type: string; variation: string; references: Set<string> }>
    >();

    sourcesByName.forEach(source => {
      if (!typesByScopeCategory.has(source.scopeCategory)) {
        typesByScopeCategory.set(source.scopeCategory, new Map());
      }
      const typeMap = typesByScopeCategory.get(source.scopeCategory)!;

      for (const et of source.emissionTypes) {
        const key = `${et.Type}|${et.Variation || ''}`;
        if (!typeMap.has(key)) {
          typeMap.set(key, {
            type: et.Type,
            variation: et.Variation || '',
            references: new Set(et.References)
          });
        } else {
          const existing = typeMap.get(key)!;
          et.References.forEach(ref => existing.references.add(ref));
        }
      }
    });

    this.emissionTypeOptions = Array.from(typesByScopeCategory.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(
      ([scopeCategory, typeMap]) => ({
        label: scopeCategory,
        value: scopeCategory,
        items: Array.from(typeMap.values()).map(et => {
          const translatedVariation = this.getTranslatedValue(
            et.variation,
            this.sourcesMap?.variations
          );
          return {
            label: translatedVariation,
            value: {
              Type: et.type,
              Variation: et.variation,
              References: Array.from(et.references)
            },
            originalValue: et.variation
          };
        }).sort((a, b) => a.label.localeCompare(b.label))
      })
    );
  }

  onEmissionTypeChange(): void {
    this.referenceOptions = [];
    this.selectedReference = '';

    if (!this.selectedEmissionType?.value?.References) return;

    this.referenceOptions = [...this.selectedEmissionType.value.References]
      .sort((a, b) => a.localeCompare(b))
      .map(ref => ({
        label: this.getTranslatedValue(ref, this.sourcesMap?.references),
        value: ref
      }));

    if (this.referenceOptions.length === 1) {
      this.selectedReference = this.referenceOptions[0].value;
    }
  }

  async handleSearch(): Promise<void> {
    const canChange = await this.unsavedChangesService.canDeactivateAsync();
    if (!canChange) {
      return;
    }

    this.isSubmitted = true;

    if (this.selectedEmissionType &&
      this.selectedReference &&
      this.selectedYear
    ) {
      const mapKey = `${this.selectedEmissionType.value.Type}|${this.selectedEmissionType.value.Variation}|${this.selectedReference}`;
      const emissionSourceId = this.globalEmissionTypeSourceMap.get(mapKey) || '';

      this.search.emit({
        emissionSource: emissionSourceId,
        emissionType: this.selectedEmissionType.value.Type,
        variation: this.selectedEmissionType.value.Variation,
        reference: this.selectedReference,
        pcg: this.selectedGlobalWarmingPotential,
        year: this.selectedYear
      });

      this.isSubmitted = false;
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Todos los campos son obligatorios.'
      });
    }
  }

  handleAddActivity(): void {
    this.addNewActivity.emit();
  }

  handleDownload(): void {
    this.download.emit();
  }

  handleDownloadTemplate(): void {
    this.downloadTemplate.emit();
  }

  handleUploadExcel(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.uploadExcel.emit(file);
    }
    // Resetear el valor para permitir seleccionar el mismo archivo de nuevo
    event.target.value = '';
  }

  onGwpChange(): void {
    if (this.selectedGlobalWarmingPotential) {
      this.gwpChange.emit(this.selectedGlobalWarmingPotential);
    }
  }

  resetFilters(): void {
    this.selectedEmissionSource = null;
    this.selectedEmissionType = null;
    this.selectedReference = '';
    this.referenceOptions = [];
    this.isSubmitted = false;
  }
}
